import * as React from "react";
import { Slot } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

import { IconTile } from "./icon-tile";

const listCardVariants = cva(
  "flex min-w-0 flex-col divide-y divide-line-1 overflow-hidden rounded-xl bg-elevated ring-1",
  {
    variants: {
      tone: {
        neutral: "ring-line-2",
        accent: "ring-accent-line",
        warn: "ring-warn-line",
        danger: "ring-danger-line"
      }
    },
    defaultVariants: { tone: "neutral" }
  }
);

type ListCardProps = React.ComponentProps<"div"> &
  VariantProps<typeof listCardVariants>;

/**
 * Cursor's integrations and settings list: one `rounded-xl` card whose rows
 * are split by `line-1` dividers. A tone ring is for approvals and warnings
 * (the Requests lint banner), never decoration.
 */
function ListCard({ tone, className, ...props }: ListCardProps) {
  return (
    <div
      data-slot="list-card"
      className={cn(listCardVariants({ tone }), className)}
      {...props}
    />
  );
}

type ListRowProps = Omit<React.ComponentProps<"div">, "title"> & {
  icon?: React.ComponentType<{ className?: string }>;
  /** Replaces the icon tile (a logo, avatar or `FileTypeIcon`). */
  media?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Right-aligned control: a button, switch, select, chevron or badge. */
  control?: React.ReactNode;
  /** `settings` rows are `px-4 py-3`; `list` rows are `p-4`. */
  density?: "settings" | "list";
  /**
   * Render as the child element (a link) and add a hover fill; the child's
   * own children are replaced by the row layout.
   */
  asChild?: boolean;
  interactive?: boolean;
};

function ListRow({
  icon,
  media,
  title,
  description,
  control,
  density = "settings",
  asChild = false,
  interactive = false,
  className,
  children,
  ...props
}: ListRowProps) {
  const Comp = asChild ? Slot.Root : "div";
  const body = (
    <>
      {media ?? (icon ? <IconTile icon={icon} /> : null)}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="truncate text-base text-fg">{title}</div>
        {description ? (
          <div className="text-base text-fg-2">{description}</div>
        ) : null}
        {asChild ? null : children}
      </div>
      {control ? (
        <div className="flex shrink-0 items-center gap-2">{control}</div>
      ) : null}
    </>
  );
  return (
    <Comp
      data-slot="list-row"
      className={cn(
        "flex min-w-0 items-center gap-3 text-left",
        density === "settings" ? "px-4 py-3" : "p-4",
        (interactive || asChild) &&
          "cursor-pointer transition-colors duration-100 outline-none hover:bg-fill-1 focus-visible:bg-fill-1",
        className
      )}
      {...props}
    >
      {asChild && React.isValidElement<{ children?: React.ReactNode }>(children)
        ? React.cloneElement(children, undefined, body)
        : body}
    </Comp>
  );
}

/** A settings form section: the ListCard outline without row dividers. */
const formCardClassName =
  "flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2";

export { ListCard, ListRow, formCardClassName, listCardVariants };
export type { ListCardProps, ListRowProps };
